import { EVENT_EXPERIMENT_VIEWED, type GrowthBook } from '@growthbook/growthbook';
import { buildAnonymousTimestampJitterMs } from '@/lib/analytics/anonymous/jitter';
import { GROWTHBOOK_PRIVATE_ATTRIBUTE_KEYS } from '@/lib/analytics/growthbook/attributes';
import logger from '@/lib/analytics/growthbook/logger';
import { NEXT_PUBLIC_GROWTHBOOK_INGESTOR_HOST } from '@/lib/env';
import { reportErrorToNewRelic } from '@/lib/newrelic/reportError';

const INGESTION_BATCH_SIZE = 100;
const INGESTION_FLUSH_INTERVAL_MS = 1000;

type QueuedGrowthBookEvent = {
  endpoint: string;
  eventName: string;
  properties: Record<string, unknown>;
  attributes: Record<string, unknown>;
  timestampJitterMs?: number;
  resolve: () => void;
  reject: (error: unknown) => void;
};

const GROWTHBOOK_ANONYMOUS_ATTRIBUTE_KEYS = new Set<string>(GROWTHBOOK_PRIVATE_ATTRIBUTE_KEYS);
const ingestionQueue: QueuedGrowthBookEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | undefined;
let flushInProgress = false;
let visibilityListenerRegistered = false;

const getAnonymousGrowthBookAttributes = (growthbook: GrowthBook): Record<string, unknown> => {
  const attributes = growthbook.getAttributes();

  return Object.fromEntries(
    Object.entries(attributes).filter(([key]) => !GROWTHBOOK_ANONYMOUS_ATTRIBUTE_KEYS.has(key))
  );
};

async function flushQueuedGrowthBookEvents(): Promise<void> {
  if (flushInProgress || ingestionQueue.length === 0) {
    return;
  }

  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = undefined;
  }

  flushInProgress = true;
  const batch = ingestionQueue.splice(0, INGESTION_BATCH_SIZE);
  const [{ endpoint }] = batch;
  const sentAtMs = Date.now();
  let requestBuilt = false;
  let responseStatus: number | undefined;

  try {
    const body = JSON.stringify({
      sentAt: new Date(sentAtMs).toISOString(),
      events: batch.map(({ eventName, properties, attributes, timestampJitterMs }) => ({
        event_name: eventName,
        properties,
        attributes,
        ...(timestampJitterMs && {
          timestamp: new Date(sentAtMs - timestampJitterMs).toISOString(),
        }),
      })),
    });
    requestBuilt = true;

    const response = await fetch(endpoint, {
      method: 'POST',
      body,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'omit',
      keepalive: true,
    });
    responseStatus = response.status;
    if (!response.ok) {
      throw new Error(`GrowthBook ingestion failed with status ${response.status}`);
    }
    batch.forEach(({ resolve }) => resolve());
  } catch (error) {
    const ingestionError =
      error instanceof Error ? error : new Error('Unknown GrowthBook ingestion error');

    // A built request that never received a status was lost at the network level (page unload, ad/tracker blockers, dropped connections) — expected for fire-and-forget beacons and not worth reporting. A non-OK response, or a failure before the request was even built (e.g. payload serialisation), is actionable.
    if (responseStatus !== undefined || !requestBuilt) {
      reportErrorToNewRelic(ingestionError, {
        errorSource: 'growthbookAnonymousIngestion',
        batchSize: batch.length,
        ...(responseStatus !== undefined && { responseStatus }),
      });
    } else {
      logger.debug('Anonymous ingestion beacon failed at network level', ingestionError);
    }

    batch.forEach(({ reject }) => reject(ingestionError));
  } finally {
    flushInProgress = false;
    if (!flushTimer && ingestionQueue.length > 0) {
      flushTimer = setTimeout(() => {
        flushTimer = undefined;
        flushQueuedGrowthBookEvents().catch(() => undefined);
      }, INGESTION_FLUSH_INTERVAL_MS);
    }
  }
}

const scheduleQueueFlush = (): void => {
  if (flushTimer || flushInProgress || ingestionQueue.length === 0) {
    return;
  }

  flushTimer = setTimeout(() => {
    flushTimer = undefined;
    flushQueuedGrowthBookEvents().catch(() => undefined);
  }, INGESTION_FLUSH_INTERVAL_MS);
};

const registerVisibilityFlush = (): void => {
  if (
    visibilityListenerRegistered ||
    typeof document === 'undefined' ||
    !document.visibilityState
  ) {
    return;
  }

  visibilityListenerRegistered = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushQueuedGrowthBookEvents().catch(() => undefined);
    }
  });
};

export const sendAnonymousGrowthBookEventToIngestion = (
  growthbook: GrowthBook,
  eventName: string,
  properties: Record<string, unknown>,
  jitterTimestamp = false
): Promise<void> => {
  const clientKey = growthbook.getClientKey();
  if (!clientKey) {
    throw new Error('GrowthBook client key is required to send ingestion events');
  }

  const endpoint = `${NEXT_PUBLIC_GROWTHBOOK_INGESTOR_HOST}/track?client_key=${encodeURIComponent(
    clientKey
  )}`;

  registerVisibilityFlush();

  return new Promise<void>((resolve, reject) => {
    ingestionQueue.push({
      endpoint,
      eventName,
      properties,
      attributes: {
        ...getAnonymousGrowthBookAttributes(growthbook),
        sdk_language: 'js',
        sdk_version: growthbook.version,
      },
      timestampJitterMs: jitterTimestamp ? buildAnonymousTimestampJitterMs() : undefined,
      resolve,
      reject,
    });
    scheduleQueueFlush();
  });
};

/** Ingest + DevTools log. The tracking plugin still no-ops while eventFilter requires consent. */
export const emitAnonymousGrowthBookEvent = (
  growthbook: GrowthBook,
  eventName: string,
  properties: Record<string, unknown>,
  jitterTimestamp = false
): void => {
  sendAnonymousGrowthBookEventToIngestion(growthbook, eventName, properties, jitterTimestamp).catch(
    (error) => {
      logger.error('Error forwarding anonymous event to GrowthBook', error);
    }
  );

  growthbook.logEvent(eventName, properties).catch((error) => {
    logger.error('Error logging anonymous event to GrowthBook DevTools', error);
  });
};

/** Consent-off exposure: GrowthBook ingestion shape only. */
export const sendAnonymousExperimentViewed = (
  growthbook: GrowthBook,
  experimentKey: string,
  variationKey: string
): void => {
  emitAnonymousGrowthBookEvent(growthbook, EVENT_EXPERIMENT_VIEWED, {
    experimentId: experimentKey,
    variationId: variationKey,
  });
};
